/**
 * Companion Orchestrator — Pinia store
 *
 * Máquina de estados do Companheiro: idle → listening → thinking → responding → idle.
 * Coordena 5 bounded contexts via seus stores Pinia.
 *
 * HLOS-38 — Sprint 3 / AC1-S3, AC3-S3 da REQ-006.
 *
 * Constitucional:
 * - Sem dark patterns (CC-05): zero streak counter ao pausar/recusar
 * - R6 (Princípio V): presence=away suprime iniciativa automaticamente
 * - CL-09 (Explicabilidade): auditoria via CompanionState + elapsedMs
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import type {
  CompanionSnapshot,
  CompanionState,
  CompanionTurnInput,
  CompanionTurnOutput,
  PyodideCaller,
} from "./types";
import { usePresenceStore } from "../presence/store";
import { useContextStore } from "../context/store";
import { useSessionStore } from "../session/store";
import { useInitiativeStore } from "../initiative/store";
import { useConversationStore } from "../conversation/store";
import type { FocusItem } from "../session/types";
import type { CuriosityGap } from "../context/types";
import {
  captureSnapshot,
  restoreSnapshot,
  isValidSnapshot,
} from "../vault/serializer";
import { useAuthStore } from "../auth/store";

/** Construtor opcional — caller pode injetar Pyodide real ou fake. */
export interface CompanionProcessOptions {
  pyodideCaller: PyodideCaller;
}

/**
 * Processa um turno do aprendiz. Coordena 5 bounded contexts.
 *
 * Flow (AC1-S3, AC3-S3):
 *   1. presence.applySignal("keystroke", "active")
 *   2. session.openThread() (lazy init se primeiro turno)
 *   3. conversation.append("learner", text)
 *   4. session.pushFocus({kind: "user_turn", label: text}) — cap 5 FIFO
 *   5. state = thinking → chama pyodide caller
 *   6. conversation.append("companion", response)
 *   7. session.pushFocus items retornados — cap 5 FIFO
 *   8. context.addCuriosityGap items — cap 3 FIFO
 *   9. initiative: setUserInUi(true) + setHasCuriosityGaps(true) + setUserRequestedProvocation(response.userAcceptedProvocation)
 *  10. state = responding → idle (após resolver initiative)
 *
 * @returns CompanionTurnResult discriminado (nunca throw).
 */
export const useCompanionStore = defineStore("companion", () => {
  const state = ref<CompanionState>("idle");
  const lastTurnAt = ref<number | null>(null);
  const lastOutput = ref<CompanionTurnOutput | null>(null);
  const lastError = ref<string | null>(null);
  let threadCounter = 0;
  const nextThreadId = (): string => {
    threadCounter += 1;
    return `thread_${threadCounter}`;
  };

  /**
   * Pausa o Companheiro (R5). Não gera streak counter (CC-05: zero penalidade).
   * Iniciativas suprimidas enquanto paused (R6: presença = away → sem initiative).
   */
  function pause(): void {
    state.value = "paused";
    const initiative = useInitiativeStore();
    initiative.setUserInUi(false);
  }

  /** Resume de pause → idle. */
  function resume(): void {
    state.value = "idle";
    const initiative = useInitiativeStore();
    initiative.setUserInUi(true);
  }

  /**
   * Recusa iniciativa atual (R3: ask → learner).
   * Não-punitivo (CC-05). Limpa flag de provocação aceita.
   */
  function refuseCurrentInitiative(): void {
    const initiative = useInitiativeStore();
    initiative.setUserRequestedProvocation(false);
  }

  /**
   * Processa um turno do aprendiz.
   */
  async function processTurn(
    input: CompanionTurnInput,
    options: CompanionProcessOptions
  ): Promise<CompanionTurnOutput | { error: "paused" }> {
    if (state.value === "paused") {
      return { error: "paused" };
    }

    const startedAt = performance.now();

    const presence = usePresenceStore();
    const context = useContextStore();
    const session = useSessionStore();
    const initiative = useInitiativeStore();
    const conversation = useConversationStore();

    // 1. presence: mark active
    presence.applySignal("keystroke", "active");
    initiative.setUserInUi(true);

    // 2. session: ensure thread is open (lazy init)
    if (!session.currentThread) {
      session.openThread({
        id: nextThreadId(),
        title: input.text.slice(0, 60),
        focus: [],
      });
    }

    // 3. conversation: append learner turn
    // HLOS-319: propaga attachments (imagem/sketch) para o feed.
    const learnerMsg = conversation.append(
      "learner",
      input.text,
      undefined,
      input.attachments,
    );

    // 4. session: push user_turn focus (cap 5 FIFO enforced inside store)
    const userFocus: FocusItem = {
      id: `focus_${learnerMsg.id}`,
      kind: "user_turn",
      label: input.text.slice(0, 80),
    };
    session.pushFocus(userFocus);

    // 5. transition to thinking
    state.value = "thinking";

    // 6. delegate inference to Pyodide caller
    let pyResult;
    try {
      pyResult = await options.pyodideCaller(input);
    } catch (err) {
      state.value = "idle";
      lastError.value = err instanceof Error ? err.message : String(err);
      throw err; // propagado ao caller (ConversationState) que exibe mensagem neutra
    }

    // 7. conversation: append companion turn
    state.value = "responding";
    // HLOS-230 (L6): propaga offer (CC-01 affordance visível) ao Message.
    // Antes o offer ficava apenas no KernelTurnResponse e nunca chegava
    // à UI; agora é persistido no Message e renderizado por MessageBubble.
    const companionMsg = conversation.append(
      "companion",
      pyResult.response,
      pyResult.offer,
    );

    // 8. session: push kernel_signal focus items (cap 5 FIFO)
    for (const item of pyResult.focusItems) {
      session.pushFocus(item);
    }

    // 9. context: add curiosity gaps (cap 3 FIFO)
    const addedGaps: CuriosityGap[] = [];
    for (const gap of pyResult.curiosityGaps) {
      if (context.addCuriosityGap(gap)) {
        addedGaps.push(gap);
      }
    }
    initiative.setHasCuriosityGaps(addedGaps.length > 0 || context.curiosityGaps.length > 0);

    // 10. initiative: configurar flags para decideInitiative()
    initiative.setUserRequestedProvocation(pyResult.userAcceptedProvocation);

    // 11. snapshot computed
    const presenceView = presence.view();
    const thread = session.currentThread;

    const output: CompanionTurnOutput = {
      companionMessage: companionMsg,
      initiative: initiative.current,
      presence: presenceView,
      activeGoal: context.activeGoal,
      curiosityGaps: [...context.curiosityGaps],
      focusStack: thread ? [...thread.focus] : [],
      elapsedMs: performance.now() - startedAt,
    };

    lastOutput.value = output;
    lastTurnAt.value = Date.now();
    lastError.value = null;
    state.value = "idle";

    // HLOS-44: persiste sessão após turno bem-sucedido (best-effort).
    void persistSession();

    return output;
  }

  /** Snapshot leve para testes/debug. */
  function snapshot(): CompanionSnapshot {
    const session = useSessionStore();
    const initiative = useInitiativeStore();
    const context = useContextStore();
    const thread = session.currentThread;
    return {
      state: state.value,
      lastTurnAt: lastTurnAt.value,
      lastError: lastError.value,
      initiative: initiative.currentOrNull,
      threadId: thread?.id ?? null,
      focusCount: thread?.focus.length ?? 0,
      curiosityCount: context.curiosityGaps.length,
    };
  }

  function clearError(): void {
    lastError.value = null;
  }

  /**
   * Persiste estado atual dos 4 bounded contexts + companion no vault.
   * HLOS-44: chamado após cada processTurn bem-sucedido e em lock().
   *
   * Best-effort: se vault locked ou storage indisponível, log e segue.
   */
  async function persistSession(): Promise<void> {
    const auth = useAuthStore();
    if (!auth.isUnlocked) {
      console.info("[companion] persistSession skipped: vault locked");
      return;
    }
    try {
      const snapshot = captureSnapshot();
      await auth.writeVaultData({ session: snapshot });
      console.info("[companion] session persisted");
    } catch (e) {
      console.warn("[companion] persistSession failed:", e);
    }
  }

  /**
   * Restaura sessão do vault (chamado no boot após unlock).
   * HLOS-44: chamado em App.vue após auth.state === "unlocked".
   *
   * @returns true se restaurou; false se snapshot ausente/inválido.
   */
  async function restoreSession(): Promise<boolean> {
    const auth = useAuthStore();
    if (!auth.isUnlocked) {
      console.info("[companion] restoreSession skipped: vault locked");
      return false;
    }
    // HLOS-140: readVaultData() é async (VaultAPI desde HLOS-134) — await.
    const data = await auth.readVaultData();
    if (!data || !data.session || !isValidSnapshot(data.session)) {
      console.info("[companion] no valid session snapshot to restore");
      return false;
    }
    const ok = restoreSnapshot(data.session as Parameters<typeof restoreSnapshot>[0]);
    if (ok) {
      lastTurnAt.value = (data.session as { companion?: { lastTurnAt: number | null } }).companion?.lastTurnAt ?? null;
      console.info("[companion] session restored");
    }
    return ok;
  }

  return {
    state,
    lastTurnAt,
    lastOutput,
    lastError,
    processTurn,
    pause,
    resume,
    refuseCurrentInitiative,
    snapshot,
    persistSession,
    restoreSession,
    clearError,
  };
});
